import { useEffect } from "react";
import Box from "@mui/material/Box";
import FormHelperText from "@mui/material/FormHelperText";
import { TextInput, RadioButtonGroupInput } from "react-admin";
import { useFormContext, useWatch } from "react-hook-form";

export type PaymentModeInputProps = {
  /** When set, cash/cheque/online are only enabled if `amountSource` parses to a number greater than 0. */
  requirePositiveLagatAmount?: boolean;
  /** Form field watched for lagat amount (default `amount`). */
  amountSource?: string;
};

function isPositiveLagatAmount(raw: unknown): boolean {
  if (raw === "" || raw === null || raw === undefined) return false;
  const n = typeof raw === "number" ? raw : Number(raw);
  return !Number.isNaN(n) && n > 0;
}

export default function PaymentModeInput({
  requirePositiveLagatAmount = false,
  amountSource = "amount",
}: PaymentModeInputProps = {}) {
  const { setValue, getValues } = useFormContext();
  const paymentMode = useWatch({ name: "paymentMode" });
  const amountRaw = useWatch({ name: amountSource });
  const amountPositive = isPositiveLagatAmount(amountRaw);
  const paymentModeEnabled = !requirePositiveLagatAmount || amountPositive;

  useEffect(() => {
    if (!requirePositiveLagatAmount || amountPositive) return;
    const pm = getValues("paymentMode");
    const pr = getValues("paymentRef");
    const needsReset = pm !== "CASH" || (pr != null && String(pr).trim() !== "");
    setValue("paymentMode", "CASH", { shouldDirty: needsReset });
    setValue("paymentRef", "", { shouldDirty: needsReset });
  }, [requirePositiveLagatAmount, amountPositive, setValue, getValues]);

  return (
    <Box>
      <RadioButtonGroupInput
        source="paymentMode"
        choices={[
          { id: "CASH", name: "CASH" },
          { id: "ONLINE", name: "ONLINE" },
          { id: "CHEQUE", name: "CHEQUE" },
        ]}
        fullWidth
        disabled={!paymentModeEnabled}
      />
      {!paymentModeEnabled ? (
        <FormHelperText sx={{ mt: 0, mx: 1.75, color: "text.secondary" }}>
          Choose payment mode after entering a lagat amount greater than zero.
        </FormHelperText>
      ) : null}
      {paymentModeEnabled && paymentMode && paymentMode !== "CASH" && (
        <TextInput source="paymentRef" label="Transaction ID / Reference" fullWidth multiline />
      )}
    </Box>
  );
}
